import React from "react";
import { formatSubjectName } from "../../../layouts/schoolPortal/schoolPortalTheme";
import { Box, Typography, Button, Chip } from "@mui/material";
import moment from "moment";
import {
  border, textPrimary, textSecondary, font, accent,
  success, warning, error as errorColor,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { card, label as labelSx, meta as metaSx } from "./recordTheme";

/**
 * Right rail — what is still open. The only column that answers "what do I do
 * now", so it sits highest on the page and stays short.
 *
 * THE APPROVAL QUEUE
 * models/student_task.js states as a hard rule that an assistant-drafted task
 * is never auto-published — it waits for a human. That rule had no surface in
 * either client, which made it a comment rather than a rule: nothing could ever
 * be approved, so every drafted task sat invisible for ever. This is the
 * surface. Nothing else in the product can approve one.
 */

const pill = (color) => ({
  height: 17, fontSize: "10px", fontWeight: 700, fontFamily: font, borderRadius: "4px",
  backgroundColor: `${color}14`, color,
});

function Block({ title, count, children }) {
  return (
    <Box sx={{ ...card, mb: 1.25 }}>
      <Typography sx={{ ...labelSx, fontFamily: font, mb: 1 }}>
        {title}{count ? ` (${count})` : ""}
      </Typography>
      {children}
    </Box>
  );
}

export default function RecordOpenItems({ record, onApproveTask, onDismissTask, onApproveSummary, onCompleteTask, busy }) {
  const { openItems = {}, student } = record;
  // Attention first: never started, then late, then the rest. An instructor
  // scanning this wants the two that have gone wrong, not the newest four.
  const tasks = [...(openItems.tasks || [])].sort((a, b) => {
    const rank = (t) => (t.stalled ? 0 : t.overdue ? 1 : 2);
    return rank(a) - rank(b);
  });
  const queue = openItems.awaitingApproval || [];
  const next = openItems.nextSession;

  return (
    <Box>
      {/* Approval queue first — it is work that BLOCKS the student. */}
      {queue.length > 0 && (
        <Box sx={{ ...card, mb: 1.25, borderColor: `${warning}55`, backgroundColor: `${warning}08` }}>
          <Typography sx={{ ...labelSx, fontFamily: font, color: warning, mb: 1 }}>
            Awaiting your approval ({queue.length})
          </Typography>
          <Typography sx={{ ...metaSx, fontFamily: font, mb: 1, lineHeight: 1.45 }}>
            {student.name?.split(" ")[0] || "This student"} cannot see these until you sign them off.
          </Typography>
          {queue.map((q) => (
            <Box key={`${q.type}-${q.id}`} sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.6, borderTop: `1px solid ${border}` }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 600, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {q.type === "task" ? q.title : `Session summary — ${moment(q.at).format("D MMM")}`}
                </Typography>
                <Typography sx={{ ...metaSx, fontFamily: font }}>
                  {q.type === "task" ? "drafted by the assistant" : "written, not published"}
                </Typography>
              </Box>
              <Button
                size="small"
                disabled={busy}
                onClick={() => (q.type === "task" ? onApproveTask(q.id) : onApproveSummary(q.id))}
                sx={{
                  textTransform: "none", fontFamily: font, fontSize: "11px", fontWeight: 700,
                  color: "#fff", backgroundColor: success, borderRadius: "999px", px: 1.5, py: 0.15, minWidth: 0,
                  "&:hover": { backgroundColor: "#15803d" },
                }}
              >
                Approve
              </Button>
              {q.type === "task" && (
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => onDismissTask(q.id)}
                  sx={{ textTransform: "none", fontFamily: font, fontSize: "11px", color: textSecondary, minWidth: 0, px: 0.75 }}
                >
                  Discard
                </Button>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Block title="Next session">
        {next ? (
          <Box>
            <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>
              {moment(next.at).format("ddd D MMM, HH:mm")}
            </Typography>
            <Typography sx={{ ...metaSx, fontFamily: font }}>
              {next.subject ? formatSubjectName(next.subject) : "1:1 session"} · {moment(next.at).fromNow()}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, lineHeight: 1.45 }}>
            {/* This promised a prefill "from where they are weakest", which on
                a student with no answers is a promise about data that does not
                exist — and the page said "Weakest areas (0)" three blocks up. */}
            Nothing booked. Propose one from the button above.
          </Typography>
        )}
      </Block>

      {(openItems.proposals || []).length > 0 && (
        <Block title="Waiting on them" count={openItems.proposals.length}>
          {openItems.proposals.map((p) => {
            const hoursLeft = p.expiresAt
              ? Math.round((new Date(p.expiresAt) - Date.now()) / 3600000)
              : null;
            return (
              <Box key={p.id} sx={{
                display: "flex", alignItems: "flex-start", gap: 0.75, py: 0.55,
                borderTop: `1px solid ${border}`, "&:first-of-type": { borderTop: "none", pt: 0 },
              }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, lineHeight: 1.35 }}>
                    You offered {moment(p.startTime).format("ddd D MMM, HH:mm")}
                    {p.subject ? ` · ${formatSubjectName(p.subject)}` : ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.2, flexWrap: "wrap" }}>
                    {p.expired
                      ? <Chip label="EXPIRED" size="small" sx={pill(errorColor)} />
                      : hoursLeft != null && hoursLeft <= 24
                        ? <Chip label={`${Math.max(0, hoursLeft)}H LEFT`} size="small" sx={pill(warning)} />
                        : null}
                    <Typography sx={{ ...metaSx, fontFamily: font }}>
                      {p.expired
                        ? "they never answered — the slot has been released"
                        : p.expiresAt
                          ? `they have until ${moment(p.expiresAt).format("D MMM, HH:mm")} to answer`
                          : "waiting on their answer"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Block>
      )}

      <Block title="Open tasks" count={tasks.length}>
        {tasks.length ? tasks.map((t) => (
          <Box key={t.id} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, py: 0.55, borderTop: `1px solid ${border}`, "&:first-of-type": { borderTop: "none", pt: 0 } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, lineHeight: 1.35 }}>
                {t.title}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.2, flexWrap: "wrap" }}>
                {t.overdue && <Chip label="OVERDUE" size="small" sx={pill(errorColor)} />}
                {/* Set and never opened. Worth saying louder than "late": a
                    student who has answered nothing has not started, which is a
                    different conversation from one who is running behind. */}
                {t.stalled && <Chip label="NOT STARTED" size="small" sx={pill(warning)} />}
                <Typography sx={{ ...metaSx, fontFamily: font }}>
                  {t.dueAt ? `due ${moment(t.dueAt).format("D MMM")}` : "no due date"} · {t.status}
                </Typography>
              </Box>

              {/* The same numbers the student sees on their own list. A task
                  with nothing countable behind it draws no bar rather than an
                  empty one. */}
              {t.progress && (
                <Box sx={{ mt: 0.6 }}>
                  <Box sx={{ height: 4, borderRadius: 2, backgroundColor: "#F0EDE9", overflow: "hidden", maxWidth: 220 }}>
                    <Box sx={{
                      width: `${t.progress.percent}%`, height: "100%",
                      backgroundColor: t.progress.met ? success : t.progress.answered ? accent : warning,
                    }} />
                  </Box>
                  <Typography sx={{ ...metaSx, fontFamily: font, mt: 0.25 }}>
                    {t.progress.answered}/{t.progress.required} answered
                    {t.progress.accuracy != null ? ` · ${t.progress.accuracy}% right` : ""}
                    {t.progress.minAccuracy ? ` · needs ${t.progress.minAccuracy}%` : ""}
                  </Typography>
                </Box>
              )}
            </Box>
            <Button
              size="small"
              disabled={busy}
              onClick={() => onCompleteTask(t.id)}
              sx={{ textTransform: "none", fontFamily: font, fontSize: "11px", color: textSecondary, minWidth: 0, px: 0.5 }}
            >
              Done
            </Button>
          </Box>
        )) : (
          <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary }}>
            No open tasks.
          </Typography>
        )}
      </Block>

      {openItems.offersOutstanding > 0 && (
        <Block title="Offers outstanding">
          <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, lineHeight: 1.45 }}>
            {openItems.offersOutstanding} session offer{openItems.offersOutstanding === 1 ? " has" : "s have"} been shown to this
            student and not yet acted on. They appear on the history below with the evidence that triggered them.
          </Typography>
        </Block>
      )}
    </Box>
  );
}
